import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Lock, Mail, CheckCircle } from 'lucide-react';
import { trackNewsletterSignup } from '@/lib/analytics';

const EmailCaptureModal = ({ resource, isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !resource) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${BACKEND_URL}/api/v4/resources/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          resource_name: resource.title,
          category: window.location.pathname.split('/').pop() // Extract category from URL
        })
      });

      const data = await response.json();

      if (response.ok) {
        try {
          trackNewsletterSignup(
            resource.title,
            window.location.pathname.split('/').pop()
          );
        } catch { /* noop */ }
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setEmail('');
          onClose();
        }, 2000);
      } else {
        console.error('Failed to submit lead:', data);
        alert('Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative p-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Lock className="w-8 h-8" style={{color: '#B7BF72'}} />
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-center mb-2">
            Unlock Premium Resources
          </h2>
          <p className="text-center text-gray-300">
            Get early access to exclusive study materials
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          {!submitted ? (
            <>
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2" style={{borderColor: 'rgba(183, 191, 114, 0.20)'}}>
                <h3 className="font-bold text-gray-900 mb-1">{resource.title}</h3>
                <p className="text-sm text-gray-600">{resource.description}</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="pl-11 h-12 text-base"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 text-lg font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #B7BF72 0%, #a8b565 100%)',
                    color: '#000000'
                  }}
                >
                  {submitting ? 'Submitting...' : 'Get Early Access'}
                </Button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  We'll notify you when premium subscriptions launch. No spam, ever.
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                You're on the list!
              </h3>
              <p className="text-gray-600">
                We'll notify you when premium resources are available.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailCaptureModal;
